import React, { useEffect, useState } from 'react';
import FeeDisplayRow from './FeeDisplayRow';
import { Paper, TableRow, TableCell, TableContainer, Typography, Table, Stack } from '@mui/material';
import FlexLib from '@flex/lib';
import PropTypes from 'prop-types';

const feeService = new FlexLib.services.FeeService(console);
const mapDatabaseFeeSettings = FlexLib.mappers.feeService.mapDatabaseFeeSettings;
const { formatCurrency, orderFeesPostCalculation } = FlexLib.utils.feeService;

/**
 *  @typedef {{
 * baseAmount?: string,
 * paymentType?: string,
 * viewAllPrices?: boolean,
 * setCalculationResult?: (result: CalculationViewResult | DualPricingViewResult) => void | null | undefined
 * setError?: (e: Error) => void
 * feeSettings?: DatabaseFeeSettings | null | undefined
 * state?: string
 * calculate?: boolean
 * calculationResult?: CalculationViewResult | DualPricingViewResult | null
 * }} FeeDisplayProps
 *
 * NOTE: feeSettings Prop is the same value as DatabaseFeeSettings, allowing you to pass in particular fee settings
 * from the parent if they cannot be accessed via local storage.
 *
 * @param {FeeDisplayProps} props
 */
const FeeDisplay = (props) => {
  const {
    baseAmount = '0.00',
    paymentType,
    viewAllPrices,
    state,
    setCalculationResult,
    setError,
    feeSettings,
    calculate = true, // Pass in calculationResult and set to false for a display-only mode
    calculationResult,
  } = props;
  const [databaseFeeSettings, setDatabaseFeeSettings] = useState(null);
  const [feeConfig, setFeeConfig] = useState(null);
  const [options, setOptions] = useState(null);

  const [feeComponents, setFeeComponents] = useState([]);
  const [totalAmount, setTotalAmount] = useState('0.00');
  const [subTotal, setSubTotal] = useState('0.00');

  const [cashFeeComponents, setCashFeeComponents] = useState([]);
  const [cashTotalAmount, setCashTotalAmount] = useState('0.00');
  const [cashSubTotal, setCashSubTotal] = useState('0.00');

  const [cardFeeComponents, setCardFeeComponents] = useState([]);
  const [cardTotalAmount, setCardTotalAmount] = useState('0.00');
  const [cardSubTotal, setCardSubTotal] = useState('0.00');

  useEffect(() => {
    if (!calculate && calculationResult?.totalAmount) {
      const { fees, options: _options, totalAmount: _totalAmount, subTotal: _subTotal } = calculationResult;
      const normalizedFees = orderFeesPostCalculation(fees, _options);
      setFeeComponents(normalizedFees.map(mapFeesToComponents));
      setTotalAmount(_totalAmount);
      setSubTotal(_subTotal);
    }
  }, [calculationResult, calculate]);

  useEffect(() => {
    if (!calculate) return;
    // initial fee settings load
    if (!databaseFeeSettings) {
      getDatabaseFeeSettings(feeSettings);
    }
  }, []);

  useEffect(() => {
    if (!calculate) return;
    // if using fee settings and it updates
    if (feeSettings && feeSettings !== databaseFeeSettings) {
      getDatabaseFeeSettings(feeSettings);
    }
  }, [props.feeSettings]);

  useEffect(() => {
    if (!calculate) return;
    if (
      databaseFeeSettings?.virtualTerminalSettings &&
      databaseFeeSettings?.invoiceSettings &&
      typeof databaseFeeSettings?.DualFeeEnabled === 'boolean' &&
      typeof databaseFeeSettings?.applyTaxLast === 'boolean'
    ) {
      const { feeConfig: _feeConfig, options: _options } = mapDatabaseFeeSettings(databaseFeeSettings);
      setFeeConfig(_feeConfig);
      setOptions(_options);
    }
  }, [databaseFeeSettings]);

  useEffect(() => {
    if (!calculate) return;
    if (!feeConfig || !options) {
      return;
    }

    const result = calculateFees();
    if (result) {
      // Calculate applied fee
      const normalizedFees = orderFeesPostCalculation(result.fees, options);
      setFeeComponents(normalizedFees.map(mapFeesToComponents));
      setTotalAmount(result?.totalAmount);
      setSubTotal(result?.subTotal);

      if (setCalculationResult) {
        setCalculationResult(result);
      }
    }

    if (viewAllPrices && options?.dualPricing) {
      const allPricesResult = calculateFees(true);
      if (allPricesResult?.cash) {
        const cashNormalizedFees = orderFeesPostCalculation(allPricesResult.cash?.fees, options);
        setCashFeeComponents(cashNormalizedFees.map(mapFeesToComponents));
        setCashTotalAmount(allPricesResult.cash?.totalAmount);
        setCashSubTotal(allPricesResult.cash?.subTotal);
      }

      if (allPricesResult?.card) {
        const cardNormalizedFees = orderFeesPostCalculation(allPricesResult.card?.fees, options);
        setCardFeeComponents(cardNormalizedFees.map(mapFeesToComponents));
        setCardTotalAmount(allPricesResult.card?.totalAmount);
        setCardSubTotal(allPricesResult.card?.subTotal);
      }
    }
  }, [feeConfig, options, baseAmount, paymentType, state]);

  /**
   * @description Wrapper for feeService.getCalculatedFees() which calculates the fees based on the current
   * base amount, fee configuration, options, and payment type. Utilizes the feeService to compute the fee
   * breakdown and total amount.
   *
   * @returns {CalculationViewResult | DualPricingViewResult}
   */
  const calculateFees = (isGetAllPrices = false) => {
    const _baseAmount = baseAmount || '0.00';

    let result;
    try {
      if (isGetAllPrices) {
        result = feeService.getDualPricingCalculatedFees(
          _baseAmount,
          feeConfig,
          options,
          paymentType?.toLowerCase(),
          state
        );
      } else {
        result = feeService.getCalculatedFees(_baseAmount, feeConfig, options, paymentType?.toLowerCase(), state);
      }
    } catch (e) {
      console.error(e);
      if (typeof setError === 'function' && e instanceof Error) {
        let erMsg = e.message;
        if (/fails to match the required pattern/.test(e?.message)) {
          erMsg =
            'Please verify values in Fee Settings. Percent fees can have 5 decimal places. Flat fees can have 2 decimal places.';
        }
        const clientFriendlyErrorMsg = `A fee calculation error has occurred: ${erMsg} If the error persists, contact techsupport@titaniumpayments.com or call 877-972-0700.`;
        setError(new Error(clientFriendlyErrorMsg));
      }
    }

    return result;
  };

  /**
   * @description Returns individual FeeDisplayRow component for each fee in an array.
   *
   * Example:
   * ```
   * const feeComponents = feeArray.map(mapFeesToComponents)
   *
   * ```
   *
   * @param {Fee} fee
   * @param {number} i
   * @returns {React.Component}
   */
  const mapFeesToComponents = (fee, i) => {
    return <FeeDisplayRow key={`${fee.name}-${i}`} fee={fee} databaseFeeSettings={databaseFeeSettings} />;
  };

  /**
   * @description Extension for currency formatter that applies some
   * local logic.
   *
   * If calculate === true, flat fees will still show up as calculated
   * when baseAmount is 0, thus breaking submission validation on parent
   * components. ex. baseAmount $0, tax $3 will calculate to $3 total,
   * but we still want to display $0 total and disallow submission.
   *
   * If calculate === false, baseAmount is included in the calculationResult
   * provided, not through the baseAmount prop. We want to fill all
   * values.
   *
   * @param {string | number} amount
   * @returns {string}
   */
  const handleFormatCurrency = (amount) => {
    if ((!baseAmount || Number.parseFloat(baseAmount) === 0) && calculate) {
      return formatCurrency('0.00');
    } else {
      return formatCurrency(amount);
    }
  };

  /**
   * @description IF the parent component passes in feeSettings as props, they become the source of truth for
   * this component. IF the parent omits feeSettings in props, they are taken from local storage. This allows
   * for custom functionality in unauthenticated use-cases (ex. paylink).
   *
   * @param {DatabaseFeeSettings | null | undefined} [feeSettings=null]
   */
  const getDatabaseFeeSettings = (feeSettings = null) => {
    let settings = {};

    if (feeSettings) {
      settings = feeSettings;
    } else {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      settings = user.settings || {};
    }

    setDatabaseFeeSettings(settings);
  };

  const getTotalType = () => {
    const cardPaymentTypes = ['credit', 'debit', ''];
    let result = '';
    if (options?.dualPricing) {
      result = cardPaymentTypes.some((pt) => pt === paymentType?.toLowerCase()) ? 'Card ' : 'ACH ';
    }

    return result;
  };

  return (
    <>
      {viewAllPrices && options?.dualPricing ? (
        <TableContainer component={Paper}>
          <Stack direction="row">
            <Table margin={1} data-cy="cash-fee-table">
              <TableRow key={'subtotalAmount-feeDisplay'} data-cy="cash-subtotal">
                <TableCell>
                  <Typography fontWeight={'500 !important'}>ACH Subtotal:</Typography>
                </TableCell>
                <TableCell align="right" data-cy="cash-subtotal-amount">
                  <Typography fontWeight={'500 !important'}>{handleFormatCurrency(cashSubTotal)}</Typography>
                </TableCell>
              </TableRow>
              {cashFeeComponents}
              <TableRow key={'cash-totalAmount-feeDisplay'} data-cy="cash-total">
                <TableCell sx={{ borderBottom: 'none' }}>
                  <Typography
                    fontWeight={
                      ['cash', 'ach'].includes(paymentType?.toLowerCase()) ? '800 !important' : '500 !important'
                    }
                  >
                    ACH Total:
                  </Typography>
                </TableCell>
                <TableCell sx={{ borderBottom: 'none' }} align="right" data-cy="cash-total-amount">
                  <Typography
                    fontWeight={
                      ['cash', 'ach'].includes(paymentType?.toLowerCase()) ? '800 !important' : '500 !important'
                    }
                  >
                    {handleFormatCurrency(cashTotalAmount)}
                  </Typography>
                </TableCell>
              </TableRow>
            </Table>
            <Table margin={1} data-cy="card-fee-table">
              <TableRow key={'subtotalAmount-feeDisplay'} data-cy="card-subtotal">
                <TableCell>
                  <Typography fontWeight={'500 !important'}>Card Subtotal:</Typography>
                </TableCell>
                <TableCell align="right" data-cy="card-subtotal-amount">
                  <Typography fontWeight={'500 !important'}>{handleFormatCurrency(cardSubTotal)}</Typography>
                </TableCell>
              </TableRow>
              {cardFeeComponents}
              <TableRow key={'card-totalAmount-feeDisplay'} data-cy="card-total">
                <TableCell sx={{ borderBottom: 'none' }}>
                  <Typography
                    fontWeight={
                      ['credit', 'debit'].includes(paymentType?.toLowerCase()) ? '800 !important' : '500 !important'
                    }
                  >
                    Card Total:
                  </Typography>
                </TableCell>
                <TableCell sx={{ borderBottom: 'none' }} align="right" data-cy="card-total-amount">
                  <Typography
                    fontWeight={
                      ['credit', 'debit'].includes(paymentType?.toLowerCase()) ? '800 !important' : '500 !important'
                    }
                  >
                    {handleFormatCurrency(cardTotalAmount)}
                  </Typography>
                </TableCell>
              </TableRow>
            </Table>
          </Stack>
        </TableContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table margin={1}>
            <TableRow key={'subtotalAmount-feeDisplay'} data-cy="subtotal">
              <TableCell>
                <Typography fontWeight={'500 !important'}>{getTotalType()}Subtotal:</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography fontWeight={'500 !important'} data-cy="subtotal-amount">
                  {handleFormatCurrency(subTotal)}
                </Typography>
              </TableCell>
            </TableRow>
            {feeComponents}
            <TableRow key={'totalAmount-feeDisplay'} data-cy="total">
              <TableCell sx={{ borderBottom: 'none' }}>
                <Typography fontWeight={'800 !important'}>{getTotalType()}Total:</Typography>
              </TableCell>
              <TableCell sx={{ borderBottom: 'none' }} align="right">
                <Typography fontWeight={'800 !important'} data-cy="total-amount">
                  {handleFormatCurrency(totalAmount)}
                </Typography>
              </TableCell>
            </TableRow>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

FeeDisplay.propTypes = {
  baseAmount: PropTypes.string,
  paymentType: PropTypes.string,
  viewAllPrices: PropTypes.bool,
  setCalculationResult: PropTypes.func,
  setError: PropTypes.func,
  feeSettings: PropTypes.object,
  state: PropTypes.string,
  calculate: PropTypes.bool,
  calculationResult: PropTypes.shape({
    fees: PropTypes.object,
    totalAmount: PropTypes.string,
    subTotal: PropTypes.string,
    baseAmount: PropTypes.string,
    paymentType: PropTypes.string,
    state: PropTypes.string,
    options: PropTypes.object,
  }),
};

export default FeeDisplay;

/**
 * @typedef {import('../../../../types/fees').DatabaseFeeSettings} DatabaseFeeSettings
 * @typedef {import('../../../../types/fees').CalculationViewResult} CalculationViewResult
 * @typedef {import('../../../../types/fees').DualPricingViewResult} DualPricingViewResult
 * @typedef {import('../../../../types/fees').Fee} Fee
 *
 */
